/***
 *
 *   MODAL
 *   Display an overlay modal anywhere in your application by calling
 *   context.modal.show() with an object containing the following props.
 *
 *   Styling matches ui-reference/src/components/ui/dialog.tsx. Focus trap,
 *   Escape handling and scroll lock come from useOverlay — Radix provided
 *   those in the reference and we are not adding it.
 *
 *   PROPS
 *   title: (optional)
 *   text: message to the user (optional)
 *   form: a form object (optional: see form docs for more information)
 *   url: destination to send the form
 *   method: HTTP post type
 *   buttonText: text for the confirm button
 *   component: a component to render inside the modal (optional)
 *   width: override the max width (default 32rem, the reference's max-w-lg)
 *
 **********/

import { useContext } from 'react';
import { ViewContext, Form, Icon, cn } from 'components/lib';
import { useOverlay } from 'components/hooks/useOverlay';
import Style from './modal.tailwind.js';

export function Modal(props) {
    const context = useContext(ViewContext);
    const close = () => context.modal.hide(true);
    const ref = useOverlay({ onClose: close });

    return (
        <>
            <div className={Style.overlay} onClick={close} aria-hidden />

            <div className={Style.wrapper}>
                <div
                    ref={ref}
                    role="dialog"
                    aria-modal="true"
                    aria-label={props.title || 'Dialog'}
                    tabIndex={-1}
                    className={cn(Style.content, !props.width && 'max-w-lg')}
                    style={props.width ? { maxWidth: props.width } : undefined}>
                    <button type="button" className={Style.close} onClick={close} aria-label="Close">
                        <Icon image="x" size={16} color="currentColor" />
                    </button>

                    {(props.title || props.text) && (
                        <header className={Style.header}>
                            {props.title && <h3 className={Style.title}>{props.title}</h3>}
                            {props.text && <p className={Style.text}>{props.text}</p>}
                        </header>
                    )}

                    {props.form && (
                        <Form
                            method={props.method}
                            url={props.url}
                            data={props.form}
                            headers={props.headers}
                            destructive={props.destructive}
                            buttonText={props.buttonText}
                            callback={props.callback}
                            updateOnChange={props.updateOnChange}
                            onChange={props.onChange}
                            submitOnConfirm={props.submitOnConfirm}
                            beforeSubmit={props.beforeSubmit}
                            injectedData={props.injectedData}
                            cancel={close}
                        />
                    )}

                    {props.component && props.component}
                </div>
            </div>
        </>
    );
}
